import React, {
  useCallback, useEffect, useState,
} from 'react';
import { Link, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { IoChevronBack, IoLocationSharp } from 'react-icons/io5';

import { getCenterAction, getDrugsAction, getPathologiesAction } from '../../actions/contentActions';
import { getProjectsAction } from '../../actions/projectsActions';
import ServiceView from '../../components/Centers/ServiceView/ServiceView';
import styles from './CenterView.module.scss';

export default function CenterView() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const center = useSelector((state) => state.content.center);
  const drugsList = useSelector((state) => state.content.drugs) || [];
  const pathologiesList = useSelector((state) => state.content.pathologies) || [];
  const projectsList = useSelector((state) => state.projects.projectsList) || [];
  const [products, setProducts] = useState([]);
  const [pathologies, setPathologies] = useState([]);

  const projectsOptions = projectsList.map((p) => ({
    value: p,
    label: p.name,
    type: p.type,
    centers: p.centers,
  }));

  const load = useCallback(() => {
    if (!id) return;
    getCenterAction(dispatch, id);
    getDrugsAction(dispatch);
    getPathologiesAction(dispatch);
    getProjectsAction(dispatch);
  }, [dispatch, id]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!center?.services || !drugsList.length) {
      setProducts([]);
      setPathologies([]);
      return;
    }
    const drugs = [];
    const centerPathologies = [];
    center.services.forEach((s) => {
      s.drugs?.forEach((d) => {
        const found = drugsList.find((dr) => dr._id === d);
        if (found && !drugs.find((f) => f._id === d)) {
          drugs.push(found);
        }
      });
      s.pathologies?.forEach((p) => {
        const found = pathologiesList.find((pa) => pa._id === p.pathology);
        if (found && !centerPathologies.find((f) => f._id === p.pathology)) {
          centerPathologies.push(found);
        }
      });
    });
    setProducts(drugs);
    setPathologies(centerPathologies);
  }, [center, drugsList, pathologiesList]);

  return (
    <div className={styles.container}>
      <header>
        <div className={styles.title}>
          <h2>{center?.name || 'Centre'}</h2>
          <Link to="/majee/centres" className={styles.back}>
            <IoChevronBack size={18} />
            Retour à la liste
          </Link>
        </div>
      </header>

      {center?._id && (
        <main className={styles.content}>
          <div className={styles.mainCol}>
            <div className={styles.card}>
              <h1>{center.name}</h1>
              <section className={styles.block}>
                <h3>Adresse</h3>
                <div className={styles.row}>
                  <div className={styles.address}>
                    <IoLocationSharp size={20} />
                    <p>
                      {center?.location?.address}
                      <br />
                      {center?.location?.postcode}
                      {' '}
                      {center?.location?.city}
                    </p>
                  </div>
                  {(center?.location?.lat || center?.location?.lon) && (
                    <div>
                      <label>Latitude :</label>
                      {' '}
                      <span>{center?.location?.lat}</span>
                      <br />
                      <label>Longitude :</label>
                      {' '}
                      <span>{center?.location?.lon}</span>
                    </div>
                  )}
                </div>
              </section>

              <section className={styles.block}>
                <h3>Responsables</h3>
                <div className={styles.row}>
                  {center?.msl && (
                    <div>
                      <label>Responsable MSL</label>
                      <p>{center.msl}</p>
                    </div>
                  )}
                  {center?.kam && (
                    <div>
                      <label>Responsable KAM</label>
                      <p>{center.kam}</p>
                    </div>
                  )}
                  {center?.rrh && (
                    <div>
                      <label>Responsable RRH</label>
                      <p>{center.rrh}</p>
                    </div>
                  )}
                </div>
              </section>
            </div>

            {center?.gers?.length > 0 && (
              <div className={styles.card}>
                <h3>GERS</h3>
                <div className={styles.gers}>
                  <div className={`${styles.gersRow} ${styles.gersHead}`}>
                    <span>Produit</span>
                    <span>cm3</span>
                    <span>cma</span>
                    <span>pdm</span>
                  </div>
                  {center.gers.map((g) => (
                    <div key={g._id || g.product} className={styles.gersRow}>
                      <span>{g.product}</span>
                      <span>{g.cm3}</span>
                      <span>{g.cma}</span>
                      <span>
                        {(parseFloat(g.pdm) * 100).toFixed(2)}
                        {' '}
                        %
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {center?.services?.map((s) => (
              <ServiceView
                key={s._id}
                service={s}
                pathologies={pathologiesList.filter(
                  (p) => s.pathologies?.find((pa) => pa.pathology === p._id),
                )}
                drugs={(s.drugs || []).map((d) => drugsList.find((dr) => dr._id === d))}
                projects={projectsOptions?.filter(
                  (p) => p?.centers?.find((c) => c.service === s?._id),
                )}
              />
            ))}
          </div>

          <aside className={styles.aside}>
            <div className={styles.card}>
              <h3>Produits</h3>
              <div className={styles.sideList}>
                {products?.map((p) => (
                  <Link key={p._id} to={`/majee/produits/${p._id}`}>{p.name}</Link>
                ))}
                {!products?.length && <p>—</p>}
              </div>
            </div>
            <div className={styles.card}>
              <h3>Pathologies</h3>
              <div className={styles.sideList}>
                {pathologies?.map((p) => (
                  <Link key={p._id} to={`/majee/pathologies/${p._id}`}>{p.name}</Link>
                ))}
                {!pathologies?.length && <p>—</p>}
              </div>
            </div>
          </aside>
        </main>
      )}
    </div>
  );
}
